/**
 * CSV export, client-side.
 *
 * The rows are already in the page - every report screen has fetched and rendered
 * them - so an export endpoint would be a second implementation of the same query,
 * free to disagree with what the user is looking at. This exports exactly what is
 * on screen, which is the only thing anyone means by "export this report".
 */

/**
 * Excel is the destination for essentially all of these, and it has two habits worth
 * defending against: it treats a leading `=`, `+`, `-` or `@` as a formula, and it
 * needs a BOM to read UTF-8 (without it, employee names with accents arrive mangled).
 */
function cell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const text = String(value);
  const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\n\r]/.test(safe) ? `"${safe.replaceAll('"', '""')}"` : safe;
}

export function toCsv(headers: string[], rows: unknown[][]): string {
  return [headers, ...rows].map((row) => row.map(cell).join(',')).join('\r\n');
}

/** Trigger a download of `rows` as `<filename>-YYYY-MM-DD.csv`. */
export function downloadCsv(filename: string, headers: string[], rows: unknown[][]): void {
  const blob = new Blob(['﻿', toCsv(headers, rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  // Revoked on the next tick, not inline: Firefox and Safari read the blob
  // asynchronously after click(), and tearing the URL down synchronously cancels
  // the download. Chrome happens to tolerate it, which is how this survives testing.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
